'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tag, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TagAutocompleteProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  maxTags?: number;
  placeholder?: string;
  className?: string;
}

export default function TagAutocomplete({
  selectedTags,
  onTagsChange,
  maxTags = 10,
  placeholder = 'Add tags...',
  className
}: TagAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  // `open` is user intent only — opened by focus/typing, closed by selection,
  // Escape, pointer-down outside, or focus leaving the subtree. The suggestion
  // list itself is derived, never stored. Previously the two were conflated in
  // one `showSuggestions` state that an effect re-asserted on every dep change,
  // so a dismissed dropdown popped straight back open while the input had text.
  const [open, setOpen] = useState(false);
  const [commonTags, setCommonTags] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch common tags on mount
  useEffect(() => {
    const fetchCommonTags = async () => {
      try {
        const response = await fetch('/api/tags?limit=20&sort=usage');
        const data = await response.json();
        if (data.tags) {
          setCommonTags(data.tags.map((t: any) => t.tag));
        }
      } catch (error) {
        console.error('Error fetching common tags:', error);
      }
    };
    fetchCommonTags();
  }, []);

  // Suggestions are derived: matches when there's input, most-common tags when
  // empty. Never stored, so nothing can re-open the dropdown behind the user.
  const suggestions = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    return commonTags
      .filter(
        (tag) =>
          !selectedTags.includes(tag) && (!query || tag.toLowerCase().includes(query))
      )
      .slice(0, 10);
  }, [inputValue, commonTags, selectedTags]);

  // Dismiss on pointer-down outside the input+dropdown subtree. `pointerdown`
  // (not `mousedown`) so touch dismisses too. It fires before `click`, but the
  // containment check means a tap on a suggestion never reaches this — the
  // selection click is not swallowed.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  // Dismiss when focus leaves the subtree entirely (Tab out). A focus move to
  // the suggestion buttons keeps `relatedTarget` inside the container, so
  // keyboard-reachable selection still works.
  const handleFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && containerRef.current?.contains(next)) return;
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setOpen(true);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  const addTag = (tag: string) => {
    const normalizedTag = tag.trim().toLowerCase().replace(/\s+/g, '-');
    if (
      normalizedTag &&
      !selectedTags.includes(normalizedTag) &&
      selectedTags.length < maxTags
    ) {
      onTagsChange([...selectedTags, normalizedTag]);
      setInputValue('');
      setOpen(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Escape' && open) {
      // Close the dropdown but keep focus and the typed text — Escape dismisses
      // the suggestions, it doesn't cancel the field.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const handleSuggestionClick = (tag: string) => {
    addTag(tag);
  };

  return (
    <div ref={containerRef} onBlur={handleFocusOut} className={cn('relative', className)}>
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTags.map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="bg-blue-600/20 border-blue-600 text-blue-300 px-2 py-1 text-xs flex items-center gap-1"
            >
              <Tag className="h-3 w-3" />
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 hover:text-red-400"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length >= maxTags ? `Max ${maxTags} tags` : placeholder}
          disabled={selectedTags.length >= maxTags}
          className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
        />

        {/* Suggestions Dropdown */}
        {open && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="p-2">
              <div className="text-xs text-slate-400 mb-2 px-2">Suggestions</div>
              {suggestions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  // Keep focus on the input so the pointer-down never blurs the
                  // field out from under the click that selects this tag.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionClick(tag)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-700 rounded text-sm text-white flex items-center gap-2"
                >
                  <Tag className="h-4 w-4 text-blue-400" />
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-slate-500 mt-1">
        {selectedTags.length}/{maxTags} tags • Press Enter to add
      </p>
    </div>
  );
}

